import { useEffect, useState, useContext, useCallback } from "react";
import DataTable from "../../utils/DataTable";
import SkeletonLoading from "../../utils/Loading/SkeletonLoading";
import ShopContext from "../../../context/ShopContext";
import ShopsHelper from "../../../helpers/ShopsHelper";
import styles from "./styles.module.css";
import EmptyState from "../../utils/EmptyState/EmptyState";
import * as ReportHelper from "helpers/ReportHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import Button from "@mui/material/Button";
import PrinterImg from "images/svg/printer.svg";
import { INVOICE_FORMATS } from "const";

export default function ItemSummaryReport({ shiftId }) {
  const { shop } = useContext(ShopContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const rows = await ReportHelper.getItemsSummaryForShift(shiftId);
      setRows(rows || []);
    } catch (error) {
      console.error("Failed to fetch Item Summary:", error);
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const headerData = [
    {
      label: "PLU",
      id: "plu",
      type: "text",
    },
    {
      label: "Item Name",
      id: "name",
      type: "text",
      width: "350px",
    },
    {
      label: "Sold Quantity",
      id: "sold_quantity",
      type: "callback",
      viewRender: (item) => {
        if (item.sold_quantity === "" || isNaN(item.sold_quantity)) {
          return item.sold_quantity;
        }
        return `${ShopsHelper.getAmountFormatted(shop, item.sold_quantity)} ${item.unit || ""}`;
      },
    },
    {
      label: "Unit Price",
      id: "price",
      type: "floatAmount",
    },
    {
      label: "Tax(%)",
      id: "taxPercentage",
      type: "floatAmount",
      width: "70px",
    },
    {
      label: "Tax Amount",
      id: "tax",
      type: "floatAmount",
    },
    {
      label: "Additional Tax(%)",
      id: "cessPercentage",
      type: "floatAmount",
      width: "70px",
    },
    {
      label: "Additional Tax",
      id: "cess",
      type: "floatAmount",
    },
    {
      label: "Amount",
      id: "payable",
      type: "floatAmount",
    },
  ];

  const handlePrint = async () => {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;

    let reportText;
    if (
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC ||
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE
    ) {
      reportText = await ReportHelper.getItemSummaryMultiThreeInch(rows, shop, shiftId);
    } else {
      reportText = await ReportHelper.getItemSummaryReport(rows, shop, shiftId);
    }
    await ReportHelper.printReport({ reportText, fallbackFunction: window.print });
  };

  const _getItemReportSummary = (items) => {
    let totalPayable = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;

    (items || []).forEach((item) => {
      totalPayable += Number(item?.payable || 0);
      totalTax += Number(item.tax || 0);
      totalAdditionalTax += Number(item.cess || 0);
    });

    const grossTotal = items?.summary?.grossTotal ?? totalPayable;
    const totalDiscount = items?.summary?.totalDiscount ?? 0;
    const netTotal = items?.summary?.netTotal ?? Math.max(0, grossTotal - totalDiscount);

    return {
      grossTotal: ShopsHelper.getAmountFormatted(shop, grossTotal),
      totalDiscount: ShopsHelper.getAmountFormatted(shop, totalDiscount),
      rawDiscount: totalDiscount,
      netTotal: ShopsHelper.getAmountFormatted(shop, netTotal),
      totalPayable: ShopsHelper.getAmountFormatted(shop, totalPayable),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
    };
  };

  const summary = _getItemReportSummary(rows);

  return (
    <div className={styles.container}>
      <div style={{ height: "100%" }}>
        <div className={styles.headerSection}>
          <div>
            <h2 className={styles.title}>Item Summary</h2>
            <h3 className={styles.subTitle}>Key financials and status of the current shift</h3>
          </div>

          <Button
            className={`${styles.button} ${styles.printBtn}`}
            onClick={handlePrint}
            startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
          >
            Print
          </Button>
        </div>

        {loading ? (
          <SkeletonLoading isOpen={true} count={8} height={45} xs={12} />
        ) : rows.length > 0 ? (
          <>
            <DataTable
              columns={headerData}
              rows={rows}
              rowKey="id"
              summary={{
                plu: "Total",
                tax: `${shop?.currency || ""} ${summary.totalTax}`,
                cess: `${shop?.currency || ""} ${summary.totalAdditionalTax}`,
                payable: `${shop?.currency || ""} ${summary.totalPayable}`,
              }}
            />
            <div className={styles.summaryCardContainer}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryCardRow}>
                  <span className={styles.summaryCardLabel}>Item Total (Gross)</span>
                  <span className={styles.summaryCardColon}>:</span>
                  <span className={styles.summaryCardValue}>
                    {shop?.currency || ""} {summary.grossTotal}
                  </span>
                </div>
                {Number(summary.rawDiscount || 0) > 0 && (
                  <div className={styles.summaryCardRow}>
                    <span className={styles.summaryCardLabel}>Total Discount</span>
                    <span className={styles.summaryCardColon}>:</span>
                    <span className={styles.summaryCardDiscount}>
                      - {shop?.currency || ""} {summary.totalDiscount}
                    </span>
                  </div>
                )}
                <div className={`${styles.summaryCardRow} ${styles.summaryCardTotalRow}`}>
                  <span className={styles.summaryCardTotalLabel}>Net Total</span>
                  <span className={styles.summaryCardColon}>:</span>
                  <span className={styles.summaryCardTotalValue}>
                    {shop?.currency || ""} {summary.netTotal}
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <EmptyState title="Item Summary" />
        )}
      </div>
    </div>
  );
}
